import styled from 'styled-components'
import Comment from '../../iconsV2/Comment'
import AddStock from '../../iconsV2/AddStock'
import Disposal from '../../iconsV2/Disposal'
import StockCount from '../../iconsV2/StockCount'
import Administration from '../../iconsV2/Administration'
import Administered from '../../iconsV2/Administered'
import Discontinued from '../../iconsV2/Discontinued'
import Missed from '../../iconsV2/Missed'
import Paused from '../../iconsV2/Paused'
import NotScheduledOrNotNeeded from '../../iconsV2/NotScheduledOrNotNeeded'
import { Text } from '@lmc/ui-kit/src/general'
import { StockRecord, MedicationStockReason } from '../../types'
import {
  getActionTitle,
  getActionSubtitle,
  getAdministrationStatusTranslationKey
} from '../../utils/stockRecordUtils'

interface ActionCellProps {
  record: StockRecord
  t: (key: string) => string
}

const renderActionIcon = (type: MedicationStockReason) => {
  switch (type) {
    case MedicationStockReason.STOCK_COUNT:
    case MedicationStockReason.RECONCILIATION:
    case MedicationStockReason.INITIAL_STOCK:
      return <StockCount />
    case MedicationStockReason.DISPOSAL:
      return <Disposal />
    case MedicationStockReason.RESTOCK:
      return <AddStock />
    case MedicationStockReason.MED_ADMIN:
      return <Administration />
    case MedicationStockReason.NOT_ADMINISTERED:
      return <Comment />
    default:
      return <StockCount />
  }
}

const renderAdministrationIcon = (statusTranslationKey: string) => {
  switch (statusTranslationKey) {
    case 'ADMINISTERED':
      return <Administered />
    case 'DISCONTINUED':
      return <Discontinued />
    case 'MISSED':
      return <Missed />
    case 'PAUSED':
      return <Paused />
    case 'NOT_SCHEDULED_OR_NOT_NEEDED':
      return <NotScheduledOrNotNeeded />
    default:
      return <Administered />
  }
}

const ActionCell = ({ record, t }: ActionCellProps) => {
  if (record.type === 'administration' && record.administration) {
    const administrationStatus =
      record.administration.administrationStatus || ''
    const statusTranslationKey =
      getAdministrationStatusTranslationKey(administrationStatus)
    const statusTitle = t(statusTranslationKey)
    const isMissed = statusTranslationKey === 'MISSED'
    const isPaused = statusTranslationKey === 'PAUSED'

    return (
      <ActionCellContainer>
        <AdministrationIconContainer $isMissed={isMissed} $isPaused={isPaused}>
          {renderAdministrationIcon(statusTranslationKey)}
        </AdministrationIconContainer>
        <TextContainer>
          <Title>{statusTitle}</Title>
        </TextContainer>
      </ActionCellContainer>
    )
  }

  const title = getActionTitle(record.action, t)
  const subtitle = getActionSubtitle(
    record.action,
    record.quantityChange || 0,
    record.unit || t('TABLETS'),
    record.notes,
    t
  )

  return (
    <ActionCellContainer>
      <IconContainer>{renderActionIcon(record.action)}</IconContainer>
      <TextContainer>
        <Title>{title}</Title>
        {subtitle && <Subtitle level={2}>{subtitle}</Subtitle>}
      </TextContainer>
    </ActionCellContainer>
  )
}

const ActionCellContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const AdministrationIconContainer = styled.div<{
  $isMissed: boolean
  $isPaused: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ $isMissed, $isPaused, theme }) =>
    $isMissed
      ? theme.color.status.danger
      : $isPaused
        ? theme.color.neutral.color5
        : theme.color.neutral.color5};

  svg {
    width: 16px;
    height: 16px;
  }
`

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const Title = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  line-height: 1;
`

const Subtitle = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

export default ActionCell
